import { createListCollection } from "@ark-ui/react";
import { type ChangeEvent, useEffect, useState } from "react";
import { Selector } from "./Selector";
import { TextField } from "./TextField";

export const countryNumberList = [
  {
    country: "KR",
    countryNumber: 82,
  },
  {
    country: "US",
    countryNumber: 1,
  },
];

type CountryInfo = {
  country: string;
  countryNumber: number;
};

const formatPhoneNumber = (inputValue: string, countryCode: "KR" | "US"): string => {
  const onlyNumber = inputValue.replace(/\D/g, "");

  if (countryCode === "KR") {
    // 한국 전화번호 형식: 010-1234-5678
    if (onlyNumber.length <= 3) {
      return onlyNumber;
    }
    if (onlyNumber.length <= 7) {
      return `${onlyNumber.slice(0, 3)}-${onlyNumber.slice(3)}`;
    }
    return `${onlyNumber.slice(0, 3)}-${onlyNumber.slice(3, 7)}-${onlyNumber.slice(7, 11)}`;
  }

  if (countryCode === "US") {
    // 미국 전화번호 형식: (206) 123-4567
    if (onlyNumber.length <= 3) {
      return onlyNumber;
    }
    if (onlyNumber.length <= 6) {
      return `(${onlyNumber.slice(0, 3)}) ${onlyNumber.slice(3)}`;
    }
    return `(${onlyNumber.slice(0, 3)}) ${onlyNumber.slice(3, 6)}-${onlyNumber.slice(6, 10)}`;
  }

  return onlyNumber;
};

function getCountryInfoFromNumber(countryNumber: number): CountryInfo {
  const foundCountryNumber = countryNumberList.find((c) => c.countryNumber === countryNumber);
  return foundCountryNumber ?? countryNumberList[0];
}

type Props = {
  countryNumber: number;
  phoneNumber: string;
  onChangeCountryNumber?: (countryNumber: number) => void;
  onChangePhoneNumber: (phoneNumber: string) => void;
  required?: boolean;
  readOnly?: boolean;
  formatRequired?: boolean;
  invalid?: boolean;
  errorMessage?: string;
  disabled?: boolean;
};

const InternationalPhoneInput = ({
  countryNumber,
  phoneNumber,
  onChangeCountryNumber,
  onChangePhoneNumber,
  required = false,
  readOnly = false,
  formatRequired = true,
  errorMessage,
  disabled = false,
}: Props) => {
  const [selectedCountryNumber, setSelectedCountryNumber] = useState<CountryInfo>(() =>
    getCountryInfoFromNumber(countryNumber),
  );
  const [phoneNumberValue, setPhoneNumberValue] = useState(phoneNumber ?? "");

  useEffect(() => {
    setSelectedCountryNumber(getCountryInfoFromNumber(countryNumber));
    setPhoneNumberValue(phoneNumber ?? "");
  }, [countryNumber, phoneNumber]);

  const collection = createListCollection<{
    value: string;
    label: string;
    countryNumber: number;
  }>({
    items: countryNumberList.map((country) => ({
      value: String(country.countryNumber),
      label: country.country,
      countryNumber: country.countryNumber,
    })),
  });

  const handleUpdateCountryNumber = (countryNumber: number) => {
    const newCountry = countryNumberList.find((country) => country.countryNumber === countryNumber);
    if (newCountry) {
      setSelectedCountryNumber(newCountry);
      setPhoneNumberValue("");
      onChangeCountryNumber?.(countryNumber);
      onChangePhoneNumber("");
    }
  };

  const handleUpdatePhoneNumber = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const onlyNumber = inputValue.replace(/\D/g, "");
    const countryCode = selectedCountryNumber.countryNumber === 82 ? "KR" : "US";

    if (countryCode === "KR" && onlyNumber.length > 11) {
      return;
    }

    if (countryCode === "US" && (onlyNumber.length > 10 || onlyNumber.startsWith("1"))) {
      return;
    }

    if (formatRequired && onlyNumber.length > 3) {
      const formattedPhoneNumber = formatPhoneNumber(inputValue, countryCode);
      setPhoneNumberValue(formattedPhoneNumber);
      onChangePhoneNumber(formattedPhoneNumber);
      return;
    }

    setPhoneNumberValue(onlyNumber);
    onChangePhoneNumber(onlyNumber);
  };

  const phoneNumberPlaceholder = selectedCountryNumber.countryNumber === 82 ? "010-1234-5678" : "(206) 123-4567";

  return (
    <div className="flex gap-1">
      <Selector
        className="w-16 bg-white"
        collection={collection}
        value={[String(selectedCountryNumber.countryNumber)]}
        onValueChange={(details) => {
          const countryNumber = Number(details.value[0]);
          handleUpdateCountryNumber(countryNumber);
        }}
        disabled={readOnly || disabled}
      >
        <Selector.Control>
          {({
            selectedItems,
          }: {
            selectedItems: {
              value: string;
              label: string;
              countryNumber: number;
              imageSrc: string;
            }[];
          }) => {
            const selectedItem = selectedItems[0];
            return <span className="inline-block mr-4">+{selectedItem?.countryNumber}</span>;
          }}
        </Selector.Control>
        <Selector.Content className="overflow-y-auto z-10 p-2 max-h-80" usePortal={false}>
          {collection.items.map((item) => (
            <Selector.Item key={item.value} item={item} className="px-3 py-2 cursor-pointer hover:bg-gray-50">
              {({ selected }) => (
                <div className="flex gap-2 justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <span className="text-sm truncate">{item.label}</span>
                    <span className="text-sm text-gray-500">+{item.value}</span>
                  </div>
                  {selected && <span className="text-primary-500">✓</span>}
                </div>
              )}
            </Selector.Item>
          ))}
        </Selector.Content>
      </Selector>

      <div className="flex-1">
        <TextField required={required} invalid={!!errorMessage} disabled={disabled}>
          <TextField.Input
            id="phoneNumber"
            name="phone_number"
            value={phoneNumberValue}
            onChange={handleUpdatePhoneNumber}
            placeholder={phoneNumberPlaceholder}
            readOnly={readOnly}
            disabled={disabled}
          />

          {errorMessage && <TextField.ErrorText>{errorMessage}</TextField.ErrorText>}
        </TextField>
      </div>
    </div>
  );
};

export default InternationalPhoneInput;
